import { BarraFerramentas, Botao, BotaoIcone, CabecalhoPagina, CampoBusca, DialogoConfirmar, EspacoBarra, EstadoVazio, Faixa, Grade, GrupoBarra, useToast, type ColunaGrade, type CorStatus, type ItemMenuSuspenso } from '@persona/ui';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import type { BuscaLista } from '../roteador';
import { Copy, Download, Eye, Inbox, PanelRight, Pencil, Plus, Printer, SearchX, Trash2 } from 'lucide-react';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { baixarArquivo, requisitar } from '../api/cliente';
import { mensagemDe } from '../api/erros';
import { useAtalho } from '../atalhos/useAtalho';
import { camposLista, podeAcao, tituloRegistro } from '../dicionario/adaptador';
import { useFavoritos } from '../navegacao/preferencias';
import { useSessao } from '../sessao/sessao';
import { acoesListaRotina } from './acoesListaRotina';
import { inclusaoRotina } from './inclusaoRotina';
import type { TelaRotina } from './tipoTelaRotina';
import { useEsquemaAtual } from './contexto';
import { FolhaImpressao, type TabelaImpressao } from './Impressao';
import { descricoesDe } from './consultas';
import { consultaLista, type Linha } from './dadosLista';
import { PainelRegistro } from './PainelRegistro';
import { colunaDe, escreverOrdem, lerOrdem, ordemServidor } from './formatacao';


const CHAVE_DETALHES = 'persona.lista.detalhes';
/** Linha de duas alturas (nome + código embaixo) quando a lista tem chave e descrição. */
const ALTURA_DUAS_LINHAS = 48;

function lerDetalhes() {
  try {
    return localStorage.getItem(CHAVE_DETALHES) !== 'fechado';
  } catch {
    return true;
  }
}

/** LISTA gerada do dicionário (07 §3.2): filtro rápido, ordenação, virtualização por blocos com cursor, teclado completo. */
export function PaginaLista() {
  const e = useEsquemaAtual();
  const AcaoExtra: TelaRotina | undefined = acoesListaRotina[e.codigo];
  // Sem strict: a lista também fica atrás da janela do registro, numa rota que não é a dela.
  // Ali a busca da rota vem vazia; a lista guarda a última que viu na própria rota (filtro e ordem não somem).
  const buscaRota = useSearch({ strict: false }) as BuscaLista;
  const naPropriaRota = useRouterState({ select: (st) => /^\/r\/[A-Z0-9]+\/?$/.test(st.location.pathname) });
  const [ultimaBusca, setUltimaBusca] = useState(buscaRota);
  if (naPropriaRota && JSON.stringify(ultimaBusca) !== JSON.stringify(buscaRota)) setUltimaBusca(buscaRota);
  const busca = naPropriaRota ? buscaRota : ultimaBusca;
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const { mostrar } = useToast();
  const { empresa, filial, estadoLicenca } = useSessao();
  const { eFavorito, alternar } = useFavoritos();
  const [filtro, setFiltro] = useState(busca.q ?? '');
  const [selecionadas, setSelecionadas] = useState<Set<string | number>>(new Set());
  const [excluir, setExcluir] = useState<Linha | null>(null);
  const [saida, setSaida] = useState<'exportar' | 'imprimir' | null>(null);
  const [folha, setFolha] = useState<TabelaImpressao | null>(null);
  const campoFiltro = useRef<HTMLInputElement>(null);
  const [focada, setFocada] = useState<Linha | undefined>(undefined);
  const [detalhes, setDetalhes] = useState(lerDetalhes);
  const alternarDetalhes = () => setDetalhes((d) => {
    try { localStorage.setItem(CHAVE_DETALHES, d ? 'fechado' : 'aberto'); } catch { /* preferência só na sessão */ }
    return !d;
  });
  const ordem = useMemo(() => {
    const o = lerOrdem(busca.o);
    return o.length ? o : e.ordemPadrao;
  }, [busca.o, e.ordemPadrao]);
  const somenteLeitura = estadoLicenca === 'somente_leitura';
  const alias = e.alias!;
  const rotina = { rotina: e.codigo };

  // Filtro rápido: debounce 250 ms, mínimo 2 caracteres (1 se numérico) — 07 §3.2.
  useEffect(() => {
    const t = setTimeout(() => {
      const q = filtro.trim();
      const valido = q.length >= 2 || /^\d+$/.test(q) || q.length === 0;
      if (valido && q !== (busca.q ?? '')) void navegar({ to: '.', search: (s) => ({ ...s, q: q || undefined, destacar: undefined }), replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [filtro, busca.q, navegar]);

  const dados = useInfiniteQuery(consultaLista({ empresa: empresa?.codigo, filial: filial?.codigo, alias, q: busca.q ?? '', ordem }));
  const linhas = useMemo(() => dados.data?.pages.flatMap((p) => p.linhas) ?? [], [dados.data]);
  const total = dados.data?.pages[0]?.total ?? 0;
  const estimado = dados.data?.pages[0]?.estimado ?? false;

  const podeIncluir = podeAcao(e, 'incluir') && !somenteLeitura;
  const podeCopiar = podeIncluir && !inclusaoRotina[e.codigo as keyof typeof inclusaoRotina];
  const podeAlterar = podeAcao(e, 'alterar') && !somenteLeitura;
  const podeExcluir = podeAcao(e, 'excluir') && !somenteLeitura;
  const novo = () => podeIncluir && void navegar({ to: '/r/$rotina/novo', params: rotina });
  const abrir = (l: Linha) => void navegar({ to: '/r/$rotina/$id', params: { ...rotina, id: String(l.id) } });
  const editar = (l: Linha | undefined) => l && podeAlterar && void navegar({ to: '/r/$rotina/$id/editar', params: { ...rotina, id: String(l.id) } });
  const copiar = (l: Linha | undefined) => l && podeCopiar && void navegar({ to: '/r/$rotina/$id/copiar', params: { ...rotina, id: String(l.id) } });

  useAtalho({ id: 'lista.incluir', teclas: ['Insert', 'Ctrl+N'], escopo: 'lista', descricao: 'Incluir', quando: () => podeIncluir && !document.querySelector('[role="dialog"]'), executar: novo });
  useAtalho({ id: 'lista.filtro', teclas: ['/', 'Ctrl+F'], escopo: 'lista', descricao: 'Filtro rápido',
    quando: () => !(document.activeElement instanceof HTMLInputElement), executar: () => campoFiltro.current?.focus() });
  useAtalho({ id: 'lista.favorito', teclas: ['Ctrl+D'], escopo: 'lista', descricao: 'Favoritar rotina', executar: () => alternar(e.codigo) });

  // Ao abrir, o foco vai para a grade: a rotina já é operável pelo teclado (07 §0 princípio 1).
  const gradeFocada = useRef(false);
  useEffect(() => {
    if (gradeFocada.current || dados.isLoading) return;
    gradeFocada.current = true;
    document.querySelector<HTMLElement>('[data-testid="grade"]')?.focus();
  }, [dados.isLoading]);

  const confirmarExclusao = async () => {
    if (!excluir) return;
    try {
      await requisitar(`/tabelas/${alias}/${excluir.id}?versao=${excluir.versao ?? 1}`, { metodo: 'DELETE' });
      mostrar({ tipo: 'ok', texto: `${tituloRegistro(e, excluir)} excluído` });
      setExcluir(null);
      await cliente.invalidateQueries({ queryKey: ['dados', empresa?.codigo, filial?.codigo, alias] });
    } catch (x) {
      const m = mensagemDe(x);
      mostrar({ tipo: 'erro', texto: m.texto, codigo: m.codigo });
      setExcluir(null);
    }
  };

  // Exportar e imprimir levam a lista inteira do recorte (busca e ordem da tela), não só o que a grade já carregou.
  const recorte = () => {
    const p = new URLSearchParams();
    if (busca.q) p.set('q', busca.q);
    const o = ordemServidor(ordem);
    if (o) p.set('o', o);
    return p;
  };
  const avisarErro = (x: unknown) => {
    const m = mensagemDe(x);
    mostrar({ tipo: 'erro', texto: m.texto, codigo: m.codigo });
  };
  const exportar = async () => {
    setSaida('exportar');
    try {
      const nome = await baixarArquivo(`/tabelas/${alias}/exportar?${recorte()}`, `${e.codigo}.csv`);
      mostrar({ tipo: 'ok', texto: `Planilha ${nome} baixada` });
    } catch (x) {
      avisarErro(x);
    } finally {
      setSaida(null);
    }
  };
  const imprimir = async () => {
    setSaida('imprimir');
    try {
      const p = recorte();
      p.set('formato', 'tabela');
      setFolha((await requisitar<TabelaImpressao>(`/tabelas/${alias}/exportar?${p}`)).dados);
    } catch (x) {
      avisarErro(x);
    } finally {
      setSaida(null);
    }
  };
  const fecharFolha = useCallback(() => setFolha(null), []);

  const descricoes = useMemo(() => descricoesDe(linhas, camposLista(e)), [linhas, e]);
  // Chave + primeiro texto viram uma coluna só, em duas linhas (nome em cima, código embaixo), como numa lista de contatos.
  const chave = e.campos.find((c) => c.chave && c.visivelEm.lista);
  const principal = e.campos.find((c) => !c.chave && c.tipo === 'texto' && c.visivelEm.lista);
  // Só em cadastro com situação e sem ação de processo (cliente, fornecedor, produto): num documento o número é o dado
  // principal e continua em coluna própria, e tabela oficial (NCM, CFOP) fica densa, uma linha por registro.
  const duasLinhas = !!e.legenda?.length && !e.acoes.some((a) => a.endpoint) && !!(chave && principal);
  // O texto seguinte (ex.: nome fantasia) desce para a linha de baixo ao lado do código: a tabela cabe sem rolar de lado.
  const complemento = duasLinhas ? e.campos.find((c) => !c.chave && c !== principal && c.tipo === 'texto' && c.visivelEm.lista && !c.mono) : undefined;
  const colunas = useMemo(() => camposLista(e).flatMap((c): ColunaGrade<Linha>[] => {
    if (duasLinhas && (c.nome === chave!.nome || c.nome === complemento?.nome)) return [];
    const coluna = colunaDe<Linha>(c, descricoes[c.nome]);
    if (duasLinhas && c.nome === principal!.nome) {
      return [{ ...coluna, largura: 'minmax(210px, 1.8fr)', secundario: (l) => {
        const vazio = (v: unknown) => v === null || v === undefined || v === '';
        const partes = [complemento && !vazio(l[complemento.nome]) ? String(l[complemento.nome]) : null,
          vazio(l[chave!.nome]) ? null : `${chave!.tituloCurto ?? chave!.titulo} ${String(l[chave!.nome])}`].filter(Boolean);
        return partes.length ? partes.join(' · ') : null;
      } }];
    }
    return [coluna];
  }), [e, descricoes, duasLinhas, chave, principal, complemento]);
  const acoesLinha = (l: Linha): ItemMenuSuspenso[] => [
    { id: 'abrir', rotulo: 'Abrir', icone: <Eye size={15} strokeWidth={1.75} />, atalho: 'Enter', aoEscolher: () => abrir(l) },
    ...(podeAlterar ? [{ id: 'alterar', rotulo: 'Alterar', icone: <Pencil size={15} strokeWidth={1.75} />, atalho: 'F4', aoEscolher: () => editar(l) }] : []),
    ...(podeCopiar ? [{ id: 'copiar', rotulo: 'Copiar', icone: <Copy size={15} strokeWidth={1.75} />, atalho: 'Ctrl+Shift+C', aoEscolher: () => copiar(l) }] : []),
    ...(podeExcluir ? [{ tipo: 'separador' as const, id: 's' }, { id: 'excluir', rotulo: 'Excluir', icone: <Trash2 size={15} strokeWidth={1.75} />, atalho: 'Del', perigo: true, aoEscolher: () => setExcluir(l) }] : []),
  ];
  // Legenda avaliada no servidor pela condição do dicionário (CF13): a linha traz { cor, rotulo }.
  const legenda = e.legenda?.length ? (l: Linha) => (l._legenda as { cor: CorStatus; rotulo: string } | undefined) ?? null : undefined;

  return (
    <div className="pagina pagina-lista">
      {folha && (
        <FolhaImpressao titulo={folha.titulo} colunas={folha.colunas} linhas={folha.linhas.map((celulas) => ({ tipo: 'linha', celulas }))}
          registros={folha.linhas.length} truncado={folha.truncado} origem={[empresa?.nome, filial?.nome].filter(Boolean).join(' · ')}
          filtro={busca.q} aoTerminar={fecharFolha} />
      )}
      <div className="lista-area" data-detalhes={detalhes || undefined}>
        <div className="lista-principal">
          <CabecalhoPagina titulo={e.titulo} codigo={e.codigo} favorito={eFavorito(e.codigo)} aoFavoritar={() => alternar(e.codigo)}
            descricao={dados.isSuccess ? `${estimado ? 'cerca de ' : ''}${total.toLocaleString('pt-BR')} ${total === 1 ? 'registro' : 'registros'}${busca.q ? ` para “${busca.q}”` : ''}` : undefined}
            acoes={(podeAcao(e, 'exportar') || AcaoExtra !== undefined) && (
              <>
                {AcaoExtra !== undefined && <Suspense fallback={null}><AcaoExtra codigo={e.codigo} /></Suspense>}
                {podeAcao(e, 'exportar') && (
                  <>
                    <Botao icone={<Printer size={15} strokeWidth={1.75} aria-hidden="true" />} carregando={saida === 'imprimir'} disabled={saida !== null || total === 0}
                      onClick={() => void imprimir()}>Imprimir</Botao>
                    <Botao icone={<Download size={15} strokeWidth={1.75} aria-hidden="true" />} carregando={saida === 'exportar'} disabled={saida !== null || total === 0}
                      onClick={() => void exportar()}>Exportar</Botao>
                  </>
                )}
              </>
            )}
            primaria={podeAcao(e, 'incluir') && (
              <Botao variante="primario" icone={<Plus size={15} strokeWidth={2} aria-hidden="true" />} atalho="Insert" onClick={novo} disabled={somenteLeitura}
                title={somenteLeitura ? 'Licença vencida — sistema em modo só leitura' : undefined}>Incluir</Botao>
            )}
          />
          {dados.isError && (
            <Faixa tipo="erro" codigo={mensagemDe(dados.error).codigo} acao={<Botao tamanho="p" onClick={() => void dados.refetch()}>Tentar de novo</Botao>}>
              {mensagemDe(dados.error).texto}
            </Faixa>
          )}
          <section className="lista-cartao" aria-label={e.titulo}>
            <BarraFerramentas rotulo={`Ferramentas de ${e.titulo}`} className="lista-cartao-barra">
              <CampoBusca ref={campoFiltro} className="lista-busca" rotulo="Filtro rápido" valor={filtro} aoMudar={setFiltro} atalho="/"
                placeholder={e.filtroRapido.length > 0 ? `Buscar por ${e.filtroRapido.map((c) => (e.campos.find((x) => x.nome === c)?.titulo ?? c).toLowerCase()).join(', ')}` : `Buscar ${e.titulo.toLowerCase()}`}
                data-testid="filtro-rapido"
                onKeyDown={(x) => {
                  if (x.key === 'Escape') (document.querySelector('[data-testid="grade"]') as HTMLElement | null)?.focus();
                  if (x.key === 'ArrowDown' || x.key === 'Enter') { x.preventDefault(); (document.querySelector('[data-testid="grade"]') as HTMLElement | null)?.focus(); }
                }} />
              <EspacoBarra />
              <BotaoIcone rotulo={detalhes ? 'Esconder detalhes' : 'Mostrar detalhes'} aria-pressed={detalhes} className="lista-alternar-detalhes"
                icone={<PanelRight size={17} strokeWidth={1.75} />} onClick={alternarDetalhes} />
            </BarraFerramentas>
          <Grade<Linha>
            rotulo={e.titulo}
            colunas={colunas}
            linhas={linhas}
            total={total}
            chave={(l) => l.id}
            carregando={dados.isFetching}
            aoPrecisarDeMais={dados.hasNextPage ? () => void dados.fetchNextPage() : undefined}
            ordem={ordem}
            aoOrdenar={(campo, adicionar) => {
              const atual = ordem.find((o) => o.campo === campo);
              const proxima = atual ? (atual.direcao === 'asc' ? { campo, direcao: 'desc' as const } : null) : { campo, direcao: 'asc' as const };
              const base = adicionar ? ordem.filter((o) => o.campo !== campo) : [];
              const lista = proxima ? [...base, proxima].slice(-3) : base;
              void navegar({ to: '.', search: (s) => ({ ...s, o: escreverOrdem(lista) }), replace: true });
            }}
            legenda={legenda}
            acoesLinha={acoesLinha}
            alturaLinha={duasLinhas ? ALTURA_DUAS_LINHAS : undefined}
            aoFocar={setFocada}
            selecionadas={selecionadas}
            aoSelecionar={setSelecionadas}
            aoAbrir={abrir}
            focoInicial={busca.destacar}
            destacar={busca.destacar}
            aoTecla={(x, l) => {
              const tecla = x.key;
              if (tecla === 'F4' || ((x.ctrlKey || x.metaKey) && tecla.toLowerCase() === 'e')) { x.preventDefault(); editar(l); }
              else if (tecla === 'Delete' && l && podeExcluir) { x.preventDefault(); setExcluir(l); }
              else if ((x.ctrlKey || x.metaKey) && x.shiftKey && tecla.toLowerCase() === 'c') { x.preventDefault(); copiar(l); }
            }}
            estadoVazio={busca.q
              ? <EstadoVazio icone={<SearchX size={32} strokeWidth={1.5} />} titulo={`Nenhum resultado para “${busca.q}”`} texto="Confira a grafia ou busque por outro campo."
                  acao={<Botao onClick={() => setFiltro('')}>Limpar busca</Botao>} />
              : <EstadoVazio icone={<Inbox size={32} strokeWidth={1.5} />} titulo={`Nenhum registro em ${e.titulo}`} texto={podeIncluir ? 'Comece incluindo o primeiro.' : undefined}
                  acao={podeIncluir ? <Botao variante="primario" onClick={novo} atalho="Insert">Incluir</Botao> : undefined} />}
          />
            <footer className="rodape-lista" aria-live="polite">
              {selecionadas.size > 0 ? (
                <GrupoBarra rotulo="Seleção">
                  <span className="selecao-resumo">{selecionadas.size} {selecionadas.size === 1 ? 'selecionado' : 'selecionados'}</span>
                  <Botao variante="fantasma" tamanho="p" onClick={() => setSelecionadas(new Set())}>Limpar seleção</Botao>
                </GrupoBarra>
              ) : (
                <span className="texto-3">Clique na caixa da linha para marcar; Shift marca um intervalo</span>
              )}
              <span className="rodape-atalhos">
                <kbd className="atalho">Enter</kbd> abrir · <kbd className="atalho">F4</kbd> alterar · <kbd className="atalho">Insert</kbd> incluir · <kbd className="atalho">Del</kbd> excluir
              </span>
            </footer>
          </section>
        </div>
        {detalhes && (
          <PainelRegistro esquema={e} campos={camposLista(e)} linha={focada && linhas.includes(focada) ? focada : undefined} descricoes={descricoes}
            titulo={focada ? String((principal && focada[principal.nome]) ?? tituloRegistro(e, focada)) : ''}
            subtitulo={focada && chave ? `${chave.tituloCurto ?? chave.titulo} ${String(focada[chave.nome] ?? '')}` : undefined}
            status={focada && legenda ? legenda(focada) : null}
            aoAbrir={focada ? () => abrir(focada) : undefined}
            aoAlterar={focada && podeAlterar ? () => editar(focada) : undefined}
            aoFechar={alternarDetalhes} />
        )}
      </div>
      <DialogoConfirmar
        aberto={!!excluir}
        titulo={`Excluir ${excluir ? tituloRegistro(e, excluir) : ''}?`}
        texto="O registro sai das listas e consultas. Quem tiver permissão ainda pode restaurá-lo."
        rotuloConfirmar="Excluir"
        perigo
        aoFechar={() => setExcluir(null)}
        aoConfirmar={confirmarExclusao}
      />
    </div>
  );
}
